import React, { useEffect, useState } from 'react';
import {
  Activity,
  AlertCircle,
  BarChart3,
  Calendar,
  Database,
  FileText,
  HardDrive,
  Loader2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import { apiClient } from '@/services/api';
import { formatFileSize } from '@/utils/fileUtils';
import { formatDurationHuman } from '@/utils/duration';
import type { TelemetryFile } from '@/types';

interface TelemetryDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  telemetryFile: TelemetryFile | null;
}

function formatDateTime(dateString?: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

function formatDuration(seconds?: number | null): string {
  if (seconds == null) return '-';
  if (seconds < 60) return `${seconds.toFixed(2)}s`;
  return formatDurationHuman(seconds);
}

function formatNumber(value?: number | null, decimals = 0): string {
  if (value == null) return '-';
  return value.toLocaleString(undefined, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  });
}

function formatSampleRate(value?: number | null): string {
  if (value == null) return '-';
  return `${formatNumber(value, 2)} Hz`;
}

function formatOffset(value?: number | null): string {
  if (value == null) return '-';
  return `${value.toFixed(3)}s`;
}

function formatAbsoluteOffset(
  recordingTimestamp?: string | null,
  offset?: number | null
): string {
  if (!recordingTimestamp || offset == null) return '-';
  const recordingStart = new Date(recordingTimestamp);
  if (Number.isNaN(recordingStart.getTime())) return '-';

  return new Date(recordingStart.getTime() + offset * 1000).toLocaleString();
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start gap-4 py-2 border-b border-secondary-100 last:border-0">
      <span className="text-sm text-secondary-500 flex-shrink-0">{label}</span>
      <span className="text-sm text-secondary-900 text-right break-all">
        {value}
      </span>
    </div>
  );
}

function DetailSection({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="text-sm font-semibold text-secondary-700 uppercase tracking-wide">
          {title}
        </h3>
      </div>
      <div className="bg-secondary-50 rounded-lg px-4">{children}</div>
    </div>
  );
}

export default function TelemetryDetailsModal({
  isOpen,
  onClose,
  telemetryFile,
}: TelemetryDetailsModalProps) {
  const { t } = useTranslation('pages');
  const [details, setDetails] = useState<TelemetryFile | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState(false);

  useEffect(() => {
    if (!isOpen || !telemetryFile) {
      setDetails(null);
      setIsLoadingDetails(false);
      setDetailsError(false);
      return;
    }

    let isCancelled = false;
    setDetails(telemetryFile);
    setDetailsError(false);
    setIsLoadingDetails(true);

    apiClient
      .getTelemetryFileDetails(telemetryFile.sessionId, telemetryFile.id)
      .then((data) => {
        if (!isCancelled) {
          setDetails(data);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setDetailsError(true);
        }
        console.error('Error fetching telemetry file details:', error);
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingDetails(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [isOpen, telemetryFile]);

  const activeTelemetryFile = details ?? telemetryFile;
  if (!activeTelemetryFile) return null;

  const channelNames =
    activeTelemetryFile.availableChannels ??
    Object.keys(activeTelemetryFile.channelMetadata ?? {}).filter(
      (name) => name !== 'Time'
    );
  const visibleChannels = channelNames.slice(0, 18);
  const hiddenChannelCount = Math.max(
    channelNames.length - visibleChannels.length,
    0
  );
  const hasTimeRange =
    activeTelemetryFile.startTime != null ||
    activeTelemetryFile.endTime != null;
  const hasParsedMetadata = activeTelemetryFile.metadataStatus === 'available';

  const formatStatus = (status: string) => {
    switch (status) {
      case 'completed':
        return t('sessions.detail.telemetry.files.status.uploaded');
      case 'uploading':
        return t('sessions.detail.telemetry.files.status.uploading');
      case 'failed':
        return t('sessions.detail.telemetry.files.status.failed');
      default:
        return t('sessions.detail.telemetry.files.status.pending');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('sessions.detail.telemetry.details.title')}
      size="lg"
    >
      <div className="space-y-6">
        {isLoadingDetails && (
          <div className="flex items-center gap-2 text-sm text-secondary-500 bg-secondary-50 rounded-lg p-3">
            <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
            <span>{t('sessions.detail.telemetry.details.loading')}</span>
          </div>
        )}

        {detailsError && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3">
            <AlertCircle className="w-4 h-4" />
            <span>{t('sessions.detail.telemetry.details.loadError')}</span>
          </div>
        )}

        <DetailSection
          icon={<FileText className="w-4 h-4 text-secondary-400" />}
          title={t(
            'sessions.detail.telemetry.details.sections.fileInformation'
          )}
        >
          <InfoRow
            label={t('sessions.detail.telemetry.details.fields.filename')}
            value={activeTelemetryFile.filename}
          />
          <InfoRow
            label={t('sessions.detail.telemetry.details.fields.fileSize')}
            value={formatFileSize(activeTelemetryFile.fileSize, {
              unknownLabel: '-',
            })}
          />
          <InfoRow
            label={t('sessions.detail.telemetry.details.fields.uploadStatus')}
            value={
              <span
                className={`capitalize ${
                  activeTelemetryFile.uploadStatus === 'completed'
                    ? 'text-green-600'
                    : activeTelemetryFile.uploadStatus === 'failed'
                      ? 'text-red-600'
                      : 'text-primary-600'
                }`}
              >
                {formatStatus(activeTelemetryFile.uploadStatus)}
              </span>
            }
          />
          <InfoRow
            label={t('sessions.detail.telemetry.details.fields.uploadedAt')}
            value={formatDateTime(activeTelemetryFile.uploadedAt)}
          />
          <InfoRow
            label={t('sessions.detail.telemetry.details.fields.createdAt')}
            value={formatDateTime(activeTelemetryFile.createdAt)}
          />
        </DetailSection>

        <DetailSection
          icon={<Calendar className="w-4 h-4 text-secondary-400" />}
          title={t('sessions.detail.telemetry.details.sections.recordingTime')}
        >
          <InfoRow
            label={t(
              'sessions.detail.telemetry.details.fields.recordingStart'
            )}
            value={formatAbsoluteOffset(
              activeTelemetryFile.recordingTimestamp,
              activeTelemetryFile.startTime ?? 0
            )}
          />
          <InfoRow
            label={t('sessions.detail.telemetry.details.fields.recordingEnd')}
            value={formatAbsoluteOffset(
              activeTelemetryFile.recordingTimestamp,
              activeTelemetryFile.endTime
            )}
          />
          {hasTimeRange && (
            <InfoRow
              label={t(
                'sessions.detail.telemetry.details.fields.telemetryRange'
              )}
              value={t('sessions.detail.telemetry.details.telemetryRange', {
                start: formatOffset(activeTelemetryFile.startTime),
                end: formatOffset(activeTelemetryFile.endTime),
              })}
            />
          )}
          <InfoRow
            label={t('sessions.detail.telemetry.details.fields.duration')}
            value={formatDuration(activeTelemetryFile.duration)}
          />
        </DetailSection>

        <DetailSection
          icon={<BarChart3 className="w-4 h-4 text-secondary-400" />}
          title={t('sessions.detail.telemetry.details.sections.dataSummary')}
        >
          <InfoRow
            label={t('sessions.detail.telemetry.details.fields.channels')}
            value={formatNumber(activeTelemetryFile.channelCount)}
          />
          <InfoRow
            label={t('sessions.detail.telemetry.details.fields.dataPoints')}
            value={formatNumber(activeTelemetryFile.dataPointCount)}
          />
          <InfoRow
            label={t('sessions.detail.telemetry.details.fields.sampleRate')}
            value={formatSampleRate(activeTelemetryFile.sampleRate)}
          />
          <InfoRow
            label={t('sessions.detail.telemetry.details.fields.laps')}
            value={formatNumber(activeTelemetryFile.lapCount)}
          />
          <InfoRow
            label={t(
              'sessions.detail.telemetry.details.fields.beaconMarkers'
            )}
            value={formatNumber(activeTelemetryFile.beaconMarkerCount)}
          />
          <InfoRow
            label={t('sessions.detail.telemetry.details.fields.segmentTimes')}
            value={formatNumber(activeTelemetryFile.segmentCount)}
          />
        </DetailSection>

        <DetailSection
          icon={<Database className="w-4 h-4 text-secondary-400" />}
          title={t('sessions.detail.telemetry.details.sections.storage')}
        >
          <InfoRow
            label={t('sessions.detail.telemetry.details.fields.s3Key')}
            value={activeTelemetryFile.s3Key}
          />
          <InfoRow
            label={t(
              'sessions.detail.telemetry.details.fields.parsedMetadata'
            )}
            value={
              hasParsedMetadata
                ? t('sessions.detail.telemetry.details.values.available')
                : t('sessions.detail.telemetry.details.values.unavailable')
            }
          />
        </DetailSection>

        {channelNames.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-4 h-4 text-secondary-400" />
              <h3 className="text-sm font-semibold text-secondary-700 uppercase tracking-wide">
                {t(
                  'sessions.detail.telemetry.details.sections.availableChannels'
                )}
              </h3>
            </div>
            <div className="bg-secondary-50 rounded-lg p-4">
              <div className="flex flex-wrap gap-2">
                {visibleChannels.map((channel) => {
                  const unit =
                    activeTelemetryFile.channelMetadata?.[channel]?.unit;
                  return (
                    <span
                      key={channel}
                      className="px-2 py-1 rounded bg-white border border-secondary-200 text-xs text-secondary-700"
                    >
                      {unit
                        ? t(
                            'sessions.detail.telemetry.details.channelWithUnit',
                            { channel, unit }
                          )
                        : channel}
                    </span>
                  );
                })}
                {hiddenChannelCount > 0 && (
                  <span className="px-2 py-1 rounded bg-secondary-100 text-xs text-secondary-600">
                    {t('sessions.detail.telemetry.details.moreChannels', {
                      count: hiddenChannelCount,
                    })}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {!hasParsedMetadata && (
          <div className="flex items-start gap-3 bg-secondary-50 rounded-lg p-4">
            <HardDrive className="w-4 h-4 text-secondary-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-secondary-600">
              {t('sessions.detail.telemetry.details.metadataUnavailable')}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
